/* Pennie theme — small client-side helpers
 * - Carousel arrows for featured collections (scroll-snap-driven)
 * - AJAX add-to-basket from product cards
 * - Drawer (mobile menu) + expandable search pill
 */

/* Remove the .is-preloading class set by the inline script in
   theme.liquid <head>. Two rAFs ensure we wait until AFTER the first
   paint with the final cascade applied — that way no transitions
   fire during the initial style settle, but transitions resume
   working for subsequent state changes. */
requestAnimationFrame(function () {
  requestAnimationFrame(function () {
    document.documentElement.classList.remove('is-preloading');
  });
});

// ---- Auto-apply the FREESHIPCA promo code ------------------------------
// Pennie wants the FREESHIPCA free-shipping code pre-applied for every
// visitor's session so Canadian customers get free shipping by default
// without needing to type the code. Customer can still remove it at
// checkout if they choose.
//
// Mechanism: Shopify's Cart AJAX API — `POST /cart/update.js` with
// `{ discount: '<code>' }` attaches the discount to the cart server-side.
// Added in Shopify Editions May 21, 2025. Works on all plans, same-origin
// (Safari-ITP safe). The discount travels with the cart token through to
// checkout where the code shows pre-applied with a removable "x".
//
// Non-Canadian visitors silently get the code attached but Shopify won't
// honor the shipping discount for non-CA destinations (FREESHIPCA's own
// destination restriction is Canada-only) — silent no-op for them.
//
// Replaces an older `/discount/<code>` approach which relied on a cookie
// that Shopify stopped setting reliably on many stores around May 2025.
//
// ⚠ Dev-loop caveat: Shopify CLI 3.89.0+ returns HTTP 401 on cart endpoints
// at localhost:9292 (acknowledged regression, Feb 3, 2026). To test locally,
// downgrade to CLI 3.88.0 or test on the live storefront. Production is
// unaffected — this code works fine in production.
//
// Idempotent + cheap: one fetch per session, response body ignored.
(function () {
  try {
    if (sessionStorage.getItem('pennie-freeshipca-applied') === '1') return;
    fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ discount: 'FREESHIPCA' }),
      credentials: 'same-origin'
    })
      .then(function (r) { if (r.ok) sessionStorage.setItem('pennie-freeshipca-applied', '1'); })
      .catch(function () { /* silent — never block UX over a discount */ });
  } catch (e) {}
})();

(function () {
  'use strict';

  // ---- Ghost-click guard --------------------------------------------------
  // iOS Safari: tapping a link navigates, then ~300ms later dispatches a
  // SYNTHESIZED "ghost" click at the same screen coordinates — on the NEW
  // page. If a product-card tap was in the top-right of the grid, that ghost
  // click lands on the freshly-loaded header's search / cart icons (they sit
  // adjacent in .header__actions) and flashes the drawer open over the
  // loading page. A genuine tap always fires pointerdown→click on the same
  // element; a ghost click arrives as a lone click with no preceding
  // pointerdown on this page. So we record the last real pointerdown and only
  // honor a toggle click that was preceded by a pointerdown on the same
  // toggle, moments earlier.
  var lastPointerDownEl = null;
  var lastPointerDownAt = 0;
  document.addEventListener('pointerdown', function (event) {
    lastPointerDownEl = event.target;
    lastPointerDownAt = Date.now();
  }, true);
  // True only when the click was preceded by a real pointerdown inside the
  // same element (selector) within the last 700ms — i.e. a genuine tap/click,
  // not a post-navigation ghost click.
  function isGenuineActivation(selector) {
    if (!lastPointerDownEl || !lastPointerDownEl.closest) return false;
    if (Date.now() - lastPointerDownAt > 700) return false;
    return !!lastPointerDownEl.closest(selector);
  }

  // ---- Image-theft client-side blockers (Step 4) --------------------------
  // Casual-deterrent layer on every <img> site-wide. Closes the four
  // common "I'll just save that" paths without any visual change to the
  // page:
  //   - contextmenu  → right-click → "Save Image As" / "Open image in new
  //                    tab" never appears (same vector Google killed in 2018
  //                    when they removed the "View image" button)
  //   - dragstart    → image cannot be dragged to the desktop or into
  //                    another tab
  //   - keydown S    → Cmd+S / Ctrl+S "Save Page As" keyboard shortcut is
  //                    suppressed (the File-menu path remains unblockable
  //                    by JS — that's an OS-level menu)
  // The CSS counterpart in base.css adds user-drag:none + user-select:none
  // + -webkit-touch-callout:none so iOS long-press also doesn't show the
  // Save Image / Add to Photos menu. Together these stop ~80% of casual
  // savers. The undefeatable floor (screenshot, mitmproxy, DevTools network
  // capture) is handled by the source cap (Step 5) + Cloudflare Worker
  // (Step 7) layers — see session-notes for the full defense-in-depth
  // stack.
  document.addEventListener('contextmenu', function (e) {
    if (e.target && e.target.tagName === 'IMG') {
      e.preventDefault();
    }
  }, { passive: false });
  document.addEventListener('dragstart', function (e) {
    if (e.target && e.target.tagName === 'IMG') {
      e.preventDefault();
    }
  }, { passive: false });
  document.addEventListener('keydown', function (e) {
    // Scope: only the literal Save shortcut. Don't touch Escape (drawer
    // close), arrow keys (carousel nav), or any modifier combo other than
    // bare Cmd/Ctrl + S.
    if ((e.metaKey || e.ctrlKey) && !e.shiftKey && !e.altKey &&
        (e.key === 's' || e.key === 'S')) {
      e.preventDefault();
    }
  }, { passive: false });

  // ---- Carousel ------------------------------------------------------------
  // Branched: mobile (≤749) uses Embla for infinite-loop center-snap; tablet
  // and desktop (≥750) use the ORIGINAL committed (c4b13ef) native scroll-snap
  // arrow paging — exactly the pre-Embla behaviour, restored. CSS in
  // featured-collection.css mirrors the same 750px breakpoint.
  //
  // Each carousel sets itself up on load AND re-sets-up on every 749px
  // breakpoint cross (matchMedia change). Without that, whichever mode was
  // active at load time stays bound while the CSS swaps to the other layout —
  // resizing dev viewport between mobile↔tablet would leave the wrong
  // engine running (Embla cards in scroll-snap layout, or vice versa).
  // Teardown uses an AbortController for the arrow listeners and `.destroy()`
  // on the Embla instance, so each setup() call starts from a clean state.
  function initCarousel(root) {
    const track = root.querySelector('[data-carousel-track]');
    const prev = root.querySelector('[data-carousel-prev]');
    const next = root.querySelector('[data-carousel-next]');
    if (!track) return;

    const mql = window.matchMedia('(max-width: 749px)');
    let embla = null;
    let abortCtrl = null;

    function teardown() {
      if (embla) { embla.destroy(); embla = null; }
      if (abortCtrl) { abortCtrl.abort(); abortCtrl = null; }
    }

    function setup() {
      teardown();
      abortCtrl = new AbortController();
      const signal = abortCtrl.signal;

      // Mobile branch — Embla infinite loop center-snap.
      if (mql.matches && typeof window.EmblaCarousel === 'function') {
        const viewport = root.querySelector('[data-carousel-viewport]');
        if (viewport) {
          embla = window.EmblaCarousel(viewport, { loop: true, align: 'center' });
          if (prev) prev.addEventListener('click', () => embla && embla.scrollPrev(), { signal });
          if (next) next.addEventListener('click', () => embla && embla.scrollNext(), { signal });
          window.addEventListener('load', () => embla && embla.reInit(), { signal });
          return;
        }
      }

      // Tablet/desktop branch — original c4b13ef native scroll-snap paging.
      function pageWidth() {
        // Scroll exactly one viewport worth of the track (which already shows N cards)
        return Math.max(track.clientWidth - 32, 320);
      }
      // Both arrows wrap: prev jumps to the end at the left edge, next jumps
      // back to the start at the right edge, so paging is continuous in both
      // directions and neither arrow ever hides.
      if (prev) prev.addEventListener('click', () => {
        if (track.scrollLeft <= 1) {
          track.scrollTo({ left: track.scrollWidth - track.clientWidth, behavior: 'smooth' });
        } else {
          track.scrollBy({ left: -pageWidth(), behavior: 'smooth' });
        }
      }, { signal });
      if (next) next.addEventListener('click', () => {
        const max = track.scrollWidth - track.clientWidth - 1;
        if (track.scrollLeft >= max) {
          track.scrollTo({ left: 0, behavior: 'smooth' });
        } else {
          track.scrollBy({ left: pageWidth(), behavior: 'smooth' });
        }
      }, { signal });
    }

    setup();
    mql.addEventListener('change', setup);
  }

  document.querySelectorAll('[data-carousel]').forEach(initCarousel);

  // ---- Catalogue dropdown menu (desktop) ---------------------------------
  // The "Catalogue" link in the header nav is rendered as a button with
  // data-catalogue-toggle. Clicking it opens the .catalogue-menu panel
  // below the header. Click the X (data-catalogue-close), or click
  // outside the panel, or press Esc to close. Mobile/tablet hides the
  // panel via CSS; the grid lives inside the hamburger drawer there.
  function closeCatalogueMenu() {
    var menu = document.querySelector('[data-catalogue-menu]');
    if (!menu) return;
    menu.classList.remove('is-open');
    menu.setAttribute('aria-hidden', 'true');
    var backdrop = document.querySelector('[data-catalogue-backdrop]');
    if (backdrop) backdrop.classList.remove('is-open');
    document.body.classList.remove('has-catalogue-open');
    document.querySelectorAll('[data-catalogue-toggle]').forEach(function (btn) {
      btn.setAttribute('aria-expanded', 'false');
    });
  }
  function openCatalogueMenu() {
    var menu = document.querySelector('[data-catalogue-menu]');
    if (!menu) return;
    menu.classList.add('is-open');
    menu.setAttribute('aria-hidden', 'false');
    var backdrop = document.querySelector('[data-catalogue-backdrop]');
    if (backdrop) backdrop.classList.add('is-open');
    document.body.classList.add('has-catalogue-open');
    document.querySelectorAll('[data-catalogue-toggle]').forEach(function (btn) {
      btn.setAttribute('aria-expanded', 'true');
    });
  }
  document.addEventListener('click', function (event) {
    // Toggle button click
    var toggle = event.target.closest('[data-catalogue-toggle]');
    if (toggle) {
      var menu = document.querySelector('[data-catalogue-menu]');
      var isOpen = menu && menu.classList.contains('is-open');
      if (isOpen) { closeCatalogueMenu(); } else { openCatalogueMenu(); }
      return;
    }
    // Explicit close button (X inside the panel)
    if (event.target.closest('[data-catalogue-close]')) {
      closeCatalogueMenu();
      return;
    }
    // Outside click — if menu is open and click is outside both the
    // menu panel AND any toggle button, close.
    var menu = document.querySelector('[data-catalogue-menu]');
    if (menu && menu.classList.contains('is-open')
        && !event.target.closest('[data-catalogue-menu]')
        && !event.target.closest('[data-catalogue-toggle]')) {
      closeCatalogueMenu();
    }
  });
  document.addEventListener('keydown', function (event) {
    if (event.key === 'Escape') closeCatalogueMenu();
  });

  // ---- Drawer (mobile/hamburger menu) -------------------------------------
  // Drawer is a native <details data-pennie-drawer> element. <summary> opens
  // it; we wire close-button, ESC, link click, and outside-click to close.
  // Closing is animated: JS adds .is-closing (which runs the slide-out
  // keyframe), waits for it to finish, then actually removes [open].

  const DRAWER_CLOSE_MS = 320;   // matches the tablet/mobile menu drawer slide (header.css @media ≤989) so the [open] removal lands at the end of the animation. Desktop still runs the 240ms CSS close animation, but it has long finished by 320ms — the extra 80ms wait is invisible because the drawer is already at its end-state (translateX(-100%)).

  function closeDrawer(drawer) {
    if (!drawer || !drawer.hasAttribute('open') || drawer.classList.contains('is-closing')) return;
    drawer.classList.add('is-closing');
    setTimeout(function () {
      drawer.removeAttribute('open');
      drawer.classList.remove('is-closing');
    }, DRAWER_CLOSE_MS);
  }

  document.addEventListener('click', function (event) {
    // Close button inside any drawer
    const closeBtn = event.target.closest('[data-drawer-close]');
    if (closeBtn) {
      closeDrawer(closeBtn.closest('[data-pennie-drawer]'));
      event.preventDefault();
      return;
    }
    // Click on a drawer link → close after navigation begins
    const drawerLink = event.target.closest('[data-pennie-drawer] a');
    if (drawerLink) {
      closeDrawer(drawerLink.closest('[data-pennie-drawer]'));
      return;
    }
    // Outside-click: close any open drawers if click is outside the panel
    document.querySelectorAll('[data-pennie-drawer][open]').forEach(function (d) {
      if (!d.contains(event.target)) closeDrawer(d);
    });
  });

  document.addEventListener('keydown', function (event) {
    if (event.key === 'Escape') {
      document.querySelectorAll('[data-pennie-drawer][open]').forEach(closeDrawer);
      closeSearch();
    }
  });

  // ---- Header search ----------------------------------------------
  // Trigger button (in actions cluster) toggles a sibling panel that
  // contains the input + clear-X.
  //   - Click trigger → open + focus input
  //   - Click trigger again with text → submit
  //   - Click trigger again with empty input → close
  //   - Click clear-X → empty the input, keep open, refocus
  //   - Click outside with empty input → close
  //   - ESC → close
  const searchPanel = document.querySelector('[data-search-form]');
  const searchToggle = document.querySelector('[data-search-toggle]');
  const searchInput = searchPanel && searchPanel.querySelector('input[type="search"]');
  const searchClear = searchPanel && searchPanel.querySelector('[data-search-clear]');

  function openSearch() {
    if (!searchPanel) return;
    searchPanel.classList.add('is-open');
    searchPanel.setAttribute('aria-hidden', 'false');
    // Flips the header to solid paper + ink on tablet/mobile (CSS, ≤989px) so
    // the nav stays legible behind the dropped full-width search bar.
    document.body.classList.add('has-search-open');
    searchToggle && searchToggle.setAttribute('aria-expanded', 'true');
    if (searchInput) {
      searchInput.removeAttribute('tabindex');
      setTimeout(() => searchInput.focus(), 60);
    }
  }
  function closeSearch() {
    if (!searchPanel) return;
    searchPanel.classList.remove('is-open');
    searchPanel.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('has-search-open');
    searchToggle && searchToggle.setAttribute('aria-expanded', 'false');
    searchInput && searchInput.setAttribute('tabindex', '-1');
    // Mobile/tablet modal: clear the query on close so reopening starts fresh
    // (the results dropdown clears via search-suggest.js when .is-open drops).
    if (searchInput && window.matchMedia('(max-width: 989px)').matches) {
      searchInput.value = '';
    }
  }
  function syncClear() {
    if (!searchClear || !searchInput) return;
    searchClear.classList.toggle('is-visible', searchInput.value.length > 0);
    if (searchInput.value.length > 0) searchClear.removeAttribute('hidden');
    else searchClear.setAttribute('hidden', '');
  }

  if (searchToggle) {
    searchToggle.addEventListener('click', function (event) {
      event.preventDefault();
      // Ignore post-navigation ghost clicks (see ghost-click guard up top).
      if (!isGenuineActivation('[data-search-toggle]')) return;
      if (searchPanel.classList.contains('is-open')) {
        if (searchInput.value.trim()) {
          searchPanel.submit();
        } else {
          closeSearch();
        }
      } else {
        openSearch();
      }
    });
  }

  if (searchInput) {
    searchInput.addEventListener('input', syncClear);
  }
  if (searchClear) {
    searchClear.addEventListener('click', function (event) {
      event.preventDefault();
      event.stopPropagation();
      searchInput.value = '';
      syncClear();
      searchInput.focus();
    });
  }

  // Outside-click closes the search ONLY if the input is empty
  document.addEventListener('mousedown', function (event) {
    if (!searchPanel || !searchPanel.classList.contains('is-open')) return;
    if (searchPanel.contains(event.target) || (searchToggle && searchToggle.contains(event.target))) return;
    if (!searchInput.value.trim()) closeSearch();
  });

  // Tablet/mobile modal: the dim backdrop or the close (X) button always closes
  // the search (regardless of input content).
  document.addEventListener('click', function (event) {
    if (event.target.closest('[data-search-backdrop]') || event.target.closest('[data-search-close]')) {
      closeSearch();
    }
  });

  // ---- Sticky header on scroll --------------------------------------------
  // Header is `position: absolute` over the hero. Once the user scrolls past
  // its original bottom edge, switch to `.header--stuck` (fixed, paper bg,
  // slide-down animation). Toggling instead of always-fixed avoids layout
  // shift and keeps the transparent-over-hero look.

  (function stickyHeader() {
    const header = document.querySelector('[data-pennie-header]');
    if (!header) return;
    // The header is `position: fixed` always (CSS). On scroll we:
    //   1. Slide its top from 42px → 0 as the announcement bar scrolls
    //      out of view (no empty gap).
    //   2. Toggle .header--stuck when the hero heading reaches the
    //      header's bottom edge — switches bg/text from transparent +
    //      white to paper + ink.
    const announcementBar = document.querySelector('.announcement-bar');
    // Publish announcement-bar height as a CSS variable so the header's
    // default absolute `top` matches the bar's actual rendered height
    // (which grows on iOS Safari to include safe-area-inset-top). Without
    // this, the absolute → fixed handover snaps because CSS top (42px) and
    // the JS pin point (announcementHeight, ~90px on iPhone) disagree.
    function publishAnnouncementHeight() {
      const h = announcementBar ? announcementBar.offsetHeight : 42;
      document.documentElement.style.setProperty('--announcement-height', `${h}px`);
      return h;
    }
    let announcementHeight = publishAnnouncementHeight();
    window.addEventListener('resize', () => { announcementHeight = publishAnnouncementHeight(); });
    const triggerEl = document.querySelector('.pennie-hero__heading');
    let stuck = false;
    let lastTop = -1;

    function onScroll() {
      // Position is handled entirely by CSS now: .header-section is
      // `position: sticky; top: var(--announcement-height)`, so the header
      // pins under the announcement bar without any JS mode-switching.
      // The only thing left for JS here is flipping the .header--stuck
      // class once the hero heading meets the bottom edge of the header.

      // flip to solid variant once the hero heading touches the header.
      // On mobile (<990vp) we trigger a bit earlier — the heading hits the
      // swap point while still ~30px below the header's bottom edge — so
      // the white state appears slightly sooner in the scroll.
      const triggerOffset = window.innerWidth < 990 ? 30 : 0;
      let shouldStick;
      if (triggerEl) {
        const tRect = triggerEl.getBoundingClientRect();
        shouldStick = tRect.top <= header.offsetHeight + triggerOffset;
      } else {
        const hRect = header.getBoundingClientRect();
        shouldStick = hRect.bottom <= triggerOffset;
      }
      if (shouldStick !== stuck) {
        stuck = shouldStick;
        header.classList.toggle('header--stuck', stuck);
      }
    }

    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
  })();

  // ---- Product card AJAX add-to-basket ------------------------------------

  document.addEventListener('click', function (event) {
    const button = event.target.closest('[data-quick-add]');
    if (!button) return;
    event.preventDefault();
    if (button.disabled) return;

    const variantId = button.getAttribute('data-variant-id');
    if (!variantId) return;

    // Icon-only ATC buttons (collection browse cards) keep their SVG and
    // signal state via [data-state] instead of textContent, which would
    // otherwise destroy the child SVG.
    const iconOnly = button.hasAttribute('data-quick-add-icon');
    const original = iconOnly ? null : button.textContent;
    button.disabled = true;
    if (iconOnly) { button.setAttribute('data-state', 'adding'); }
    else { button.textContent = 'Adding…'; }

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] })
    })
      .then(r => r.json())
      .then(() => {
        if (iconOnly) { button.setAttribute('data-state', 'added'); }
        else { button.textContent = 'Added'; }
        // Refresh the drawer contents + header badge from the server, then
        // slide the cart open so the user sees what they just added.
        // __pennieRefreshCart also syncs every [data-cart-count] badge
        // (with the bump animation) so the header count stays in step.
        return window.__pennieRefreshCart().then(function () {
          window.__pennieOpenCart();
        });
      })
      .catch(() => {
        if (iconOnly) { button.setAttribute('data-state', 'error'); }
        else { button.textContent = 'Try again'; }
      })
      .then(() => {
        setTimeout(() => {
          button.disabled = false;
          if (iconOnly) { button.removeAttribute('data-state'); }
          else { button.textContent = original; }
        }, 1400);
      });
  });

  // ---- Cart drawer --------------------------------------------------------
  // Slide-out basket. The drawer markup lives in sections/cart-drawer.liquid
  // (global via header-group). We never reload the page for cart changes:
  //   - open/close toggles .is-open on [data-cart-drawer] + body scroll-lock
  //   - qty steppers / remove POST to /cart/change.js
  //   - after any mutation we re-fetch the section HTML (Section Rendering
  //     API: /?sections=cart-drawer), swap ONLY [data-cart-drawer-contents],
  //     and sync the header [data-cart-count] badge.

  function openCart() {
    var drawer = document.querySelector('[data-cart-drawer]');
    if (!drawer) return;
    drawer.classList.add('is-open');
    drawer.setAttribute('aria-hidden', 'false');
    document.body.classList.add('has-cart-open');
  }

  function closeCart() {
    var drawer = document.querySelector('[data-cart-drawer]');
    if (!drawer) return;
    drawer.classList.remove('is-open');
    drawer.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('has-cart-open');
  }

  function syncCartBadge(count) {
    document.querySelectorAll('[data-cart-count]').forEach(function (n) {
      n.textContent = count;
      if (count > 0) {
        n.removeAttribute('hidden');
        n.classList.remove('is-bumped');
        void n.offsetWidth;          // restart the bump keyframe
        n.classList.add('is-bumped');
      } else {
        n.setAttribute('hidden', '');
      }
    });
  }

  // Re-render the drawer contents from the server so prices, qty, subtotal,
  // and empty-state all stay authoritative. Returns a promise.
  function refreshCart() {
    return fetch('/?sections=cart-drawer', { headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (data) {
        var html = data['cart-drawer'];
        if (html == null) return;
        var parsed = new DOMParser().parseFromString(html, 'text/html');
        var fresh = parsed.querySelector('[data-cart-drawer-contents]');
        var live = document.querySelector('[data-cart-drawer-contents]');
        // Only swap when the markup actually changed — re-writing identical
        // innerHTML re-creates the <img> nodes and makes them flash on open.
        if (fresh && live && fresh.innerHTML !== live.innerHTML) {
          live.innerHTML = fresh.innerHTML;
        }
      })
      .then(function () {
        return fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
          .then(function (r) { return r.json(); })
          .then(function (cart) { syncCartBadge(cart.item_count); });
      });
  }
  // Expose so the add-to-cart handler (and future callers) can refresh + open.
  window.__pennieRefreshCart = refreshCart;
  window.__pennieOpenCart = openCart;

  // Open from the header cart icon (anchor kept for no-JS fallback).
  document.addEventListener('click', function (event) {
    var toggle = event.target.closest('[data-cart-toggle]');
    if (toggle) {
      event.preventDefault();
      // Ignore post-navigation ghost clicks (see ghost-click guard up top).
      // Still preventDefault above so a stray ghost click can't navigate to
      // /cart either — we just don't open the drawer for it.
      if (!isGenuineActivation('[data-cart-toggle]')) return;
      // Open using the contents already server-rendered into the page. We do
      // NOT re-fetch + swap the drawer HTML here: on the first open after a
      // page load, the section-API markup differs trivially from the inline
      // render, so the swap would re-create every <img> and they'd blink once.
      // Contents are authoritative on load; cart mutations (add / qty / remove)
      // refresh them. Just keep the count badge fresh (cheap, no DOM swap).
      openCart();
      fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
        .then(function (r) { return r.json(); })
        .then(function (cart) { syncCartBadge(cart.item_count); })
        .catch(function () {});
      return;
    }
    // Close on backdrop / close-button / "continue shopping" link.
    if (event.target.closest('[data-cart-drawer-close]')) {
      // Let continue-shopping anchors still navigate.
      var link = event.target.closest('a[data-cart-drawer-close]');
      if (!link) event.preventDefault();
      closeCart();
      return;
    }
  });

  // Signal that the JS cart handler is live. The early inline guard in
  // theme.liquid <head> prevents the cart anchor from navigating to /cart
  // before this point; if a click came in during that window it set
  // __pennieCartPending, so honour it now by opening the drawer.
  window.__pennieCartReady = true;
  if (window.__pennieCartPending) {
    window.__pennieCartPending = false;
    openCart();
    refreshCart();
  }

  // Qty steppers + remove inside the drawer. Mirrors the +/- and set=0
  // semantics, but refreshes the drawer in place instead of reloading.
  document.addEventListener('click', function (event) {
    var btn = event.target.closest('[data-cart-drawer] [data-cart-qty-change]');
    if (!btn) return;
    event.preventDefault();
    var line = parseInt(btn.getAttribute('data-cart-qty-change'), 10);
    if (!line) return;

    var lineEl = document.querySelector('[data-cart-drawer] [data-cart-line="' + line + '"]');
    if (!lineEl) return;

    var quantity;
    if (btn.hasAttribute('data-cart-qty-set')) {
      quantity = parseInt(btn.getAttribute('data-cart-qty-set'), 10);
    } else {
      var delta = parseInt(btn.getAttribute('data-cart-qty-delta'), 10) || 0;
      var current = parseInt(lineEl.querySelector('[data-cart-qty-value]').textContent, 10) || 0;
      quantity = Math.max(0, current + delta);
    }

    lineEl.querySelectorAll('[data-cart-qty-change]').forEach(function (b) { b.disabled = true; });

    fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ line: line, quantity: quantity })
    })
      .then(function (r) { return r.json(); })
      .then(function () { return refreshCart(); })
      .catch(function () {
        lineEl.querySelectorAll('[data-cart-qty-change]').forEach(function (b) { b.disabled = false; });
      });
  });

  document.addEventListener('keydown', function (event) {
    if (event.key === 'Escape') closeCart();
  });

  // Intercept the PDP "Add to bag" form so it AJAX-adds and opens the
  // drawer instead of navigating to /cart. Falls back to a normal submit
  // only if fetch is unavailable (very old browsers).
  document.addEventListener('submit', function (event) {
    var form = event.target.closest('.pdp__form');
    if (!form || !window.fetch) return;
    event.preventDefault();

    var submitBtn = form.querySelector('[type="submit"]');
    if (submitBtn && submitBtn.disabled) return;
    var originalText = submitBtn ? submitBtn.textContent : null;
    if (submitBtn) { submitBtn.disabled = true; submitBtn.textContent = 'Adding…'; }

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Accept': 'application/json' },
      body: new FormData(form)
    })
      .then(function (r) { return r.json(); })
      .then(function () { return refreshCart(); })
      .then(function () {
        openCart();
        if (submitBtn) submitBtn.textContent = 'Added';
      })
      .catch(function () {
        if (submitBtn) submitBtn.textContent = 'Try again';
      })
      .then(function () {
        setTimeout(function () {
          if (submitBtn) { submitBtn.disabled = false; submitBtn.textContent = originalText; }
        }, 1400);
      });
  });

  // ---- Product card hover/touch image swap -------------------------------
  // Hovering a card swaps to the product's SECOND image and holds there —
  // it does NOT keep cycling through the rest of the photos while you move
  // around. The card only snaps back to the featured (first) image once the
  // cursor leaves the card entirely (or touch ends).
  (function initCardHover() {
    const lastStep = new WeakMap();   // retained for the mouseout/touch resets
    const curIdx = new WeakMap();

    function setActive(media, idx) {
      const imgs = media.querySelectorAll('[data-card-image]');
      imgs.forEach((img, i) => img.classList.toggle('is-active', i === idx));
      curIdx.set(media, idx);
    }
    function step(media) {
      // Tablet + mobile (≤989px): no hover image-swap. There's no mouse, and
      // activating a stacked hover image (data-index 1, absolute inset:0) makes
      // it the visible top layer with edges on all four of .product-card__media's
      // rounded clip boundaries — which peeks a 1px image-coloured seam on the
      // card sides on mobile WebKit. Keeping only the cover (index 0) shown
      // avoids it. Gates both the mousemove (emulator) and touch paths.
      if (!window.matchMedia('(min-width: 990px)').matches) return;
      const count = parseInt(media.getAttribute('data-image-count') || '1', 10);
      if (count < 2) return;
      // Show ONLY the second image and stay on it; no further cycling.
      if (curIdx.get(media) === 1) return;
      setActive(media, 1);
    }

    document.addEventListener('mousemove', function (e) {
      const media = e.target.closest && e.target.closest('[data-card-media]');
      if (!media) return;
      step(media);
    });
    // mouseout + relatedTarget detects actually leaving the card (mouseleave
    // doesn't bubble so it's awkward to delegate).
    document.addEventListener('mouseout', function (e) {
      const media = e.target.closest && e.target.closest('[data-card-media]');
      if (!media) return;
      if (e.relatedTarget && media.contains(e.relatedTarget)) return;
      lastStep.delete(media);
      setActive(media, 0);
    });

    // Touch: dragging cycles images on the same throttle. A normal tap-and-
    // release still navigates to the PDP because the anchor click fires.
    let touchTarget = null;
    document.addEventListener('touchstart', function (e) {
      const media = e.target.closest && e.target.closest('[data-card-media]');
      if (!media) return;
      const count = parseInt(media.getAttribute('data-image-count') || '1', 10);
      if (count < 2) return;
      touchTarget = media;
    }, { passive: true });
    document.addEventListener('touchmove', function () {
      if (!touchTarget) return;
      step(touchTarget);
    }, { passive: true });
    document.addEventListener('touchend', function () {
      if (!touchTarget) return;
      const m = touchTarget;
      touchTarget = null;
      // Brief pause so the last-shown image stays visible before snapping back
      setTimeout(() => { lastStep.delete(m); setActive(m, 0); }, 250);
    });
    document.addEventListener('touchcancel', function () {
      if (touchTarget) { lastStep.delete(touchTarget); setActive(touchTarget, 0); }
      touchTarget = null;
    });
  })();

  // ---- Coordinated skeleton reveal ----------------------------------------
  // ONE controller for every section's skeleton (hero, product-card carousels,
  // image-with-text). Every skeleton stays up until the WHOLE page has loaded
  // (window 'load' — fires only once every image is actually in) AND a minimum
  // on-screen time has elapsed, then all skeletons drop together so sections
  // never pop in piecemeal.
  //
  // Carousel images ship as loading="lazy", which window 'load' ignores. Since
  // the skeleton's whole job is to cover the page until everything's in, we
  // flip those to eager so they fetch now and are counted by 'load'. CSS
  // background images (image-with-text bg-fixed) also don't count toward
  // 'load', so we drop in a hidden probe <img> with the same src.
  (function initSkeletonReveal() {
    var MIN_MS = 1500;   // minimum shimmer time (avoids a flash on cached loads)
    var targets = [];    // elements that receive .is-loaded

    document.querySelectorAll('[data-hero-card]').forEach(function (card) {
      targets.push(card);
    });
    document.querySelectorAll('.product-card').forEach(function (card) {
      if (card.querySelector('.product-card__skeleton')) targets.push(card);
    });
    // NOTE: .browse-card (collection grid) is deliberately NOT revealed here.
    // Its products stream in via background-fetch after window 'load' and get
    // re-ordered by the Featured bag-sort, so the grid owns its own unison
    // reveal in collection.liquid (gated on the fetch chain finishing).
    document.querySelectorAll('.pdp__rec-card').forEach(function (card) {
      if (card.querySelector('.pdp__rec-card-skeleton')) targets.push(card);
    });
    // The rec container itself carries the "You may also like" heading loader.
    document.querySelectorAll('.pdp__rec').forEach(function (rec) {
      if (rec.querySelector('.pdp__rec-heading-skeleton')) targets.push(rec);
    });
    document.querySelectorAll('.pdp__details').forEach(function (el) {
      if (el.querySelector('.pdp__details-skeleton')) targets.push(el);
    });
    // Top-text loader runs on mobile + desktop (the in-place skeleton works at
    // any width since it's applied to the real child elements).
    document.querySelectorAll('.pdp__top-text').forEach(function (el) {
      targets.push(el);
    });
    // PDP gallery shimmer is desktop/tablet-only (on mobile the gallery is a
    // swipe strip with no shiner — its skeleton is display:none <750px). We
    // ALWAYS track it so it ALWAYS receives .is-loaded at reveal. Otherwise a
    // page that loads on mobile (gallery skipped) and is then widened to desktop
    // shows a shiner that nothing ever dismisses — it's orphaned, and the 5s cap
    // only reveals tracked elements. Marking it loaded on mobile is harmless
    // (the skeleton is hidden there). The force-eager step below stays gated to
    // ≥750 so we still don't eager-load the mobile swipe strip.
    var galleryDesktop = window.matchMedia('(min-width: 750px)').matches;
    document.querySelectorAll('.pdp__gallery').forEach(function (gallery) {
      if (gallery.querySelector('.pdp__gallery-skeleton')) targets.push(gallery);
    });
    document.querySelectorAll('.iwt-card').forEach(function (card) {
      targets.push(card);
      var src = card.getAttribute('data-iwt-img-src');
      if (src) {
        var probe = new Image();
        probe.alt = '';
        probe.setAttribute('aria-hidden', 'true');
        probe.style.cssText = 'position:absolute;width:1px;height:1px;opacity:0;pointer-events:none;';
        probe.src = src;
        card.appendChild(probe);   // appended so it counts toward window 'load'
      }
    });

    // Force the tracked sections' lazy images to load now so 'load' waits on them.
    targets.forEach(function (card) {
      // Don't eager-load the mobile PDP gallery swipe strip (perf): it's tracked
      // only so it gets .is-loaded, but its images shouldn't all fetch upfront
      // on mobile. On desktop the gallery is eagered like everything else.
      if (!galleryDesktop && card.classList.contains('pdp__gallery')) return;
      card.querySelectorAll('img[loading="lazy"]').forEach(function (img) {
        img.loading = 'eager';
      });
    });

    var MAX_MS = 5000;   // failsafe: a single forced-eager image that hangs must
                         // never lock the page — reveal no matter what by here.
    var minDone = false, loadDone = false, revealed = false;
    function doReveal() {
      if (revealed) return;
      revealed = true;
      targets.forEach(function (t) { t.classList.add('is-loaded'); });
    }
    function maybeReveal() { if (minDone && loadDone) doReveal(); }
    setTimeout(function () { minDone = true; maybeReveal(); }, MIN_MS);
    setTimeout(doReveal, MAX_MS);   // last-resort cap (normally window 'load' wins first)
    if (document.readyState === 'complete') { loadDone = true; maybeReveal(); }
    else { window.addEventListener('load', function () { loadDone = true; maybeReveal(); }); }
  })();

  // ---- Manage Cookies link ------------------------------------------------
  // Footer "Manage Cookies" link (href="#manage-cookies") re-opens Shopify's
  // built-in cookie consent banner so visitors can change their choices after
  // their first visit. Tries the Customer Privacy API first; falls back to
  // clearing the consent cookie and reloading (which retriggers the banner).
  document.addEventListener('click', function (e) {
    const link = e.target.closest('a[href="#manage-cookies"]');
    if (!link) return;
    e.preventDefault();
    const cp = window.Shopify && window.Shopify.customerPrivacy;
    if (cp && typeof cp.showPreferences === 'function') {
      cp.showPreferences();
      return;
    }
    if (cp && typeof cp.openPreferences === 'function') {
      cp.openPreferences();
      return;
    }
    // Fallback: clear the consent cookies and reload — Shopify's banner will
    // re-evaluate and show again on the next page load.
    ['_tracking_consent', 'tracking_consent', '_shopify_y', '_shopify_s'].forEach(function (name) {
      document.cookie = name + '=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/';
    });
    window.location.reload();
  });

  // ---- Policy pages: eyebrow + footnote -----------------------------------
  // Shopify renders /policies/* with hardcoded markup and silently ignores
  // any custom theme template (policy.liquid / policies.liquid). To give
  // policy pages the editorial feel, inject an eyebrow above the title and
  // a CTA footnote at the end so the page resolves instead of cutting off.
  const policyTitle = document.querySelector('.shopify-policy__title');
  if (policyTitle && !policyTitle.querySelector('.shopify-policy__eyebrow')) {
    const eyebrow = document.createElement('p');
    eyebrow.className = 'shopify-policy__eyebrow';
    eyebrow.textContent = 'Policies';
    policyTitle.insertBefore(eyebrow, policyTitle.firstChild);
  }
  // Shopify renders policy titles like "Privacy policy" / "Terms of service"
  // (sentence case). Normalize to title case for editorial consistency,
  // keeping short connectors (of/and/the/etc.) lowercase unless they're
  // the first word.
  const policyH1 = policyTitle && policyTitle.querySelector('h1');
  if (policyH1) {
    const stops = new Set(['of', 'and', 'the', 'or', 'for', 'to', 'in', 'on', 'a', 'an']);
    policyH1.textContent = policyH1.textContent.trim().toLowerCase().split(/\s+/)
      .map((w, i) => (i > 0 && stops.has(w)) ? w : w.charAt(0).toUpperCase() + w.slice(1))
      .join(' ');
  }
})();

/* ============================================================
   PDP details tabs — horizontal tabs (Details / Size / Care).
   Liquid pre-marks the first available tab and panel as .is-active
   (no-JS fallback). Click handler swaps .is-active across the
   tabs and panels in lockstep.
   ============================================================ */
(function () {
  document.querySelectorAll('.pdp__details').forEach(function (container) {
    const tabs = container.querySelectorAll('.pdp__details-tab');
    const panels = container.querySelectorAll('.pdp__details-panel');
    if (!tabs.length) return;
    tabs.forEach(function (tab) {
      tab.addEventListener('click', function () {
        const target = tab.getAttribute('data-tab');
        tabs.forEach(function (t) { t.classList.toggle('is-active', t === tab); });
        panels.forEach(function (p) { p.classList.toggle('is-active', p.getAttribute('data-panel') === target); });
      });
    });
  });
})();

/* ============================================================
   Mobile PDP gallery scroll-indicator dots.
   Populates .pdp__gallery-dots with one <span> per gallery item,
   then updates which dot has .is-active based on the gallery's
   horizontal scroll position. Each gallery item is 100vw wide on
   mobile, so activeIndex = round(scrollLeft / viewportWidth).
   Dots are CSS-hidden on tablet+ (see main-product.css).
   ============================================================ */
(function () {
  const gallery = document.querySelector('.pdp__gallery');
  const dotsContainer = document.querySelector('.pdp__gallery-dots');
  if (!gallery || !dotsContainer) return;
  const items = gallery.querySelectorAll('.pdp__gallery-item, .pdp__gallery-placeholder');
  if (items.length < 2) return;   // single slide → no need for dots
  // Create one dot per gallery item. The dot matching the currently-
  // visible gallery item gets .is-active (ink-coloured); the rest stay
  // at the default light-grey colour. Class toggles on scroll — no
  // transition, instant snap.
  for (let i = 0; i < items.length; i++) {
    const dot = document.createElement('span');
    dot.className = 'pdp__gallery-dot';
    dotsContainer.appendChild(dot);
  }
  const dots = dotsContainer.querySelectorAll('.pdp__gallery-dot');
  function updateActive() {
    if (window.innerWidth >= 750) return;   // dots only meaningful on mobile (where gallery scrolls horizontally)
    const itemWidth = window.innerWidth;     // each item is 100vw on mobile
    const idx = Math.min(items.length - 1, Math.max(0, Math.round(gallery.scrollLeft / itemWidth)));
    dots.forEach(function (d, i) { d.classList.toggle('is-active', i === idx); });
  }
  updateActive();
  gallery.addEventListener('scroll', updateActive, { passive: true });
})();

/* ============================================================
   Mobile PDP header state triggers.

   Two body classes get toggled on the product page at mobile viewports
   to change the header's appearance based on scroll position:

     .is-gallery-past-first → user scrolled the horizontal gallery past
        the first image. Logo flips to ink. Header bg + other icons stay
        as their default (transparent + white) so there's no visible
        "white bar" during horizontal scroll.

     .is-past-gallery → user scrolled vertically far enough that the
        gallery's bottom edge is at or above the header's bottom edge.
        Full "stuck" treatment kicks in via CSS: paper bg + ink logo +
        ink icons. Matches the landing-page CTA-past behaviour.

   Both states are PDP-only and mobile-only. CSS lives in main-product.css.
   The trigger logic stops running (and removes the classes) at >=750vp.
   ============================================================ */
(function () {
  if (!document.body.classList.contains('template-product')) return;
  const gallery = document.querySelector('.pdp__gallery');
  const header = document.querySelector('[data-pennie-header]');
  if (!gallery || !header) return;

  const MOBILE_MAX = 749;
  function isMobile() { return window.innerWidth <= MOBILE_MAX; }

  function checkGalleryScroll() {
    if (!isMobile()) {
      document.body.classList.remove('is-gallery-past-first');
      return;
    }
    const firstItem = gallery.querySelector('.pdp__gallery-item, .pdp__gallery-placeholder');
    if (!firstItem) return;
    const itemWidth = firstItem.getBoundingClientRect().width;
    // Fire once the gallery is scrolled more than halfway off the first
    // image — matches the visual moment image 2 becomes more prominent.
    const past = gallery.scrollLeft > itemWidth * 0.5;
    document.body.classList.toggle('is-gallery-past-first', past);
  }

  function checkVerticalScroll() {
    if (!isMobile()) {
      document.body.classList.remove('is-past-gallery');
      return;
    }
    const eyebrow = document.querySelector('.pdp__eyebrow');
    const headerRect = header.getBoundingClientRect();
    let past;
    if (eyebrow) {
      // Fire when the eyebrow's top edge crosses the header's bottom
      // (i.e., the eyebrow is about to slip behind the header).
      past = eyebrow.getBoundingClientRect().top <= headerRect.bottom;
    } else {
      // Fallback: products without an eyebrow trigger off the gallery.
      past = gallery.getBoundingClientRect().bottom <= headerRect.bottom;
    }
    document.body.classList.toggle('is-past-gallery', past);
  }

  gallery.addEventListener('scroll', checkGalleryScroll, { passive: true });
  window.addEventListener('scroll', checkVerticalScroll, { passive: true });
  window.addEventListener('resize', function () {
    checkGalleryScroll();
    checkVerticalScroll();
  });

  // Initial run so the classes reflect the load-time state.
  checkGalleryScroll();
  checkVerticalScroll();
})();

/* ----- PDP "You may like" carousel — infinite loop via Embla Carousel -----
   Embla (self-hosted, loaded before this file on product pages) owns the drag,
   momentum and seamless infinite loop, so it works the same on every viewport
   and avoids the iOS native-scroll-snap flicker. It moves the .pdp__rec-track
   (the Embla "container") inside the overflow-hidden .pdp__rec-viewport via
   transforms — it does NOT clone DOM, so the 9 .pdp__rec-card elements (and the
   skeleton coordinator's count of them) are unchanged.
     - loop: true             → endless in both directions, capped at the 9 cards.
     - align: 'start'         → desktop/tablet 3-up, first card left-aligned.
     - align: 'center' ≤749px → mobile lands on a CENTERED card; with loop, the
                                last card peeks on the left + next on the right.
   Arrows drive scrollPrev/scrollNext (they wrap with loop). If Embla failed to
   load, we bail and the viewport just shows the first cards statically. */
(function () {
  const rec = document.querySelector('[data-pdp-rec]');
  if (!rec) return;
  const viewport = rec.querySelector('[data-pdp-rec-viewport]');
  const prev = rec.querySelector('[data-pdp-rec-prev]');
  const next = rec.querySelector('[data-pdp-rec-next]');
  if (!viewport || typeof window.EmblaCarousel !== 'function') return;

  // Match the landing carousel's pattern: branch by viewport at init time and
  // pass `align: 'center'` directly when on mobile (instead of `align: 'start'`
  // + breakpoints override). Embla's breakpoint switching can leave a brief
  // `start`-aligned render (cards packed from the left edge = "2-up flush"
  // loader flash) before flipping to `center`; passing the right align value
  // up front avoids that. Tablet + desktop still get `align: 'start'`.
  const isMobile = window.matchMedia('(max-width: 749px)').matches;
  const embla = window.EmblaCarousel(viewport, {
    loop: true,
    align: isMobile ? 'center' : 'start'
  });
  // reInit once everything has loaded so Embla re-measures slide positions
  // with images in (matches the landing carousel's `window.load → reInit`).
  if (isMobile) {
    window.addEventListener('load', () => embla.reInit());
  }

  if (prev) prev.addEventListener('click', () => embla.scrollPrev());
  if (next) next.addEventListener('click', () => embla.scrollNext());

  // Trackpad / horizontal wheel support — same debounced pattern as the landing
  // carousels above. We accumulate horizontal deltas during the gesture and fire
  // ONCE, 100ms after the wheel events stop, so a single swipe can never advance
  // more than one card. Vertical wheel falls through to the page scroll.
  let lastWheelAt = 0;
  viewport.addEventListener('wheel', (e) => {
    if (Math.abs(e.deltaX) <= Math.abs(e.deltaY)) return;
    e.preventDefault();
    const now = Date.now();
    if (now - lastWheelAt < 350) return;
    if (e.deltaX > 4) { embla.scrollNext(); lastWheelAt = now; }
    else if (e.deltaX < -4) { embla.scrollPrev(); lastWheelAt = now; }
  }, { passive: false });

  // Images decode after init can shift slide sizes; re-measure once everything's
  // loaded so loop math + alignment stay exact.
  window.addEventListener('load', () => embla.reInit());
})();
